import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import {
  SOURCES,
  PACKS,
  recommend,
  fallbackColours,
  swatchHex,
  packLabel,
  packSizes,
  resolvePack,
  variantImageFor,
  same,
  FALLBACK_UNIT,
} from '@theme/lc-watering-logic';

/**
 * <lc-pdp-buybox>
 *
 * Waterer Cups purchase flow (ported from the design's Waterer Cups PDP). Two paths:
 *  - Direct  ("I know what I need"): setup type → pack size → colour.
 *  - Guided  ("Help me choose"):     setup type → flock size → colour; the pack follows the band.
 * Both resolve to the same variant of the colour's product (container → cups product,
 * PVC → Cups & Tees product), so price, imagery and the add-to-cart line always agree with the
 * homepage configurator. Colours are theme-editor blocks; the page's own product picks the
 * default colour.
 */

/** @param {unknown} value */
const esc = (value) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c
  );

const CHECK = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#058346" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
const MINUS = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/></svg>';
const PLUS = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/><path d="M12 5v14"/></svg>';

/**
 * True when two image URLs point at the same file, ignoring protocol and query (Shopify's
 * CDN encodes the requested width and version in the query string).
 * @param {string} a
 * @param {string} b
 */
const sameImageFile = (a, b) => {
  try {
    return new URL(a, location.href).pathname === new URL(b, location.href).pathname;
  } catch {
    return a === b;
  }
};

class LcPdpBuybox extends HTMLElement {
  /** @type {{ colours: any[], currentColour: string, moneyFormat: string, currency: string, text: Record<string, string> }} */
  config = { colours: [], currentColour: '', moneyFormat: '${{amount}}', currency: 'USD', text: {} };

  state = {
    mode: /** @type {'direct' | 'guided'} */ ('direct'),
    source: /** @type {string | null} */ ('container'),
    flock: /** @type {number | null} */ (null),
    colour: /** @type {string | null} */ (null),
    pack: /** @type {number | null} */ (5),
    qty: 1,
    mEdit: /** @type {string | null} */ (null),
    busy: false,
    added: false,
    error: '',
  };

  #media = window.matchMedia('(max-width: 989px)');

  connectedCallback() {
    const script = this.querySelector('script[data-lc-config]');
    if (script?.textContent) {
      try {
        this.config = { ...this.config, ...JSON.parse(script.textContent) };
      } catch (error) {
        console.warn('[lc-pdp-buybox] invalid config', error);
      }
    }
    // A Cups & Tees product page opens on the PVC setup; a cups page on the container setup.
    this.state.source = this.config.currentSource === 'pvc' ? 'pvc' : 'container';
    this.state.colour = this.config.currentColour || this.colourEntries()[0]?.name || null;
    const first = this.productFor(this.entryFor(this.state.colour));
    const sizes = packSizes(first);
    this.state.pack = this.firstAvailablePack(this.state.colour, sizes) ?? PACKS[0];
    // A Shopify ?variant= URL (Shopping / ad landing pages) opens on that exact pack, even when
    // it is sold out, so the page agrees with the feed that linked to it.
    const requested = Number(this.config.selectedVariantId) || 0;
    if (requested && first) {
      const hit = sizes.find((n) => resolvePack(first, this.state.colour, n)?.variant.id === requested);
      if (hit) this.state.pack = hit;
    }

    this.addEventListener('click', this.#onClick);
    this.#media.addEventListener('change', () => this.render());
    this.setAttribute('data-ready', 'true');
    this.render();
    this.#watchBuyPanel();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
  }

  /** @type {IntersectionObserver | null} */
  #observer = null;
  #panelVisible = true;

  /** Show the mobile sticky bar only once the buy panel has scrolled out of view. */
  #watchBuyPanel() {
    const panel = this.querySelector('.lc-pdp__panel');
    if (!panel || !('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.#panelVisible = entry.isIntersecting;
        this.renderSticky(this.setup());
      },
      { threshold: 0 }
    );
    this.#observer.observe(panel);
  }

  /** @param {ReturnType<LcPdpBuybox['setup']>} s */
  renderSticky(s) {
    const bar = this.querySelector('[data-lc-sticky]');
    if (!(bar instanceof HTMLElement)) return;
    const st = this.state;
    const t = this.config.text;
    const show = this.isMobile && !this.#panelVisible && s.ready;
    bar.hidden = !show;
    if (!show) return;
    const total = s.price * st.qty;
    const canBuy = s.variantId && s.available && !st.busy;
    bar.innerHTML = `
      <div class="lc-pdp__sticky-text">
        <div class="lc-pdp__sticky-title">${esc(`${packLabel(s.cups)} · ${st.colour} · ${s.isPvc ? 'PVC pipe' : 'Container'}${st.qty > 1 ? ` × ${st.qty}` : ''}`)}</div>
        <div class="lc-pdp__sticky-price">${this.money(total)}</div>
      </div>
      <button type="button" class="lc-btn lc-btn--primary" data-action="add" ${canBuy ? '' : 'disabled'}>${esc(st.added ? t.addedLabel || '✓ Added to cart' : t.addLabel || 'Add to cart')}</button>`;
  }

  get isMobile() {
    return this.#media.matches;
  }

  /** @param {number} cents */
  money(cents) {
    return formatMoney(Math.round(cents), this.config.moneyFormat, this.config.currency);
  }

  /** @param {Partial<typeof this.state>} patch */
  setState(patch) {
    Object.assign(this.state, patch);
    this.render();
  }

  // ------------------------------------------------------------------ data

  colourEntries() {
    const { source } = this.state;
    const key = source === 'pvc' ? 'tees' : 'cups';
    const withProduct = this.config.colours.filter((c) => c[key]);
    if (withProduct.length) return withProduct;
    return fallbackColours(source ?? 'container').map((name) => ({ name, hex: swatchHex(name), cups: null, tees: null }));
  }

  /** @param {string | null} colour */
  entryFor(colour) {
    if (!colour) return null;
    return this.colourEntries().find((c) => same(c.name, colour)) ?? null;
  }

  /** @param {any} entry */
  productFor(entry) {
    if (!entry) return null;
    return this.state.source === 'pvc' ? entry.tees : entry.cups;
  }

  /** @param {string} colour */
  imageFor(colour) {
    const product = this.productFor(this.entryFor(colour));
    return product ? variantImageFor(product, colour) : '';
  }

  /**
   * Whether `cups` cups in `colour` can be bought right now. No product connected = preview,
   * treated as available. Used to grey out sold-out colour tiles and pack buttons.
   * @param {string | null} colour @param {number} cups
   */
  packAvailable(colour, cups) {
    if (!cups) return true;
    const product = this.productFor(this.entryFor(colour));
    if (!product) return true;
    const pack = resolvePack(product, colour, cups);
    return pack ? pack.variant.available !== false : true;
  }

  /** A pack size is dead when it is sold out for the chosen colour, or for every colour if none is chosen yet. */
  /** @param {number} cups */
  packSoldOut(cups) {
    const st = this.state;
    if (st.colour && this.entryFor(st.colour)) return !this.packAvailable(st.colour, cups);
    const entries = this.colourEntries();
    return entries.length > 0 && entries.every((c) => !this.packAvailable(c.name, cups));
  }

  /** First pack size that is in stock for `colour` (falls back to the first size). */
  /** @param {string | null} colour @param {number[]} sizes */
  firstAvailablePack(colour, sizes) {
    return sizes.find((n) => this.packAvailable(colour, n)) ?? sizes[0] ?? null;
  }

  /** Current selection resolved to a variant. */
  setup() {
    const st = this.state;
    const guided = st.mode === 'guided';
    const rec = recommend(st.source, guided ? st.flock : null);
    const entry = this.entryFor(st.colour) ?? this.colourEntries()[0] ?? null;
    const product = this.productFor(entry);
    const colourOk = Boolean(st.colour && this.entryFor(st.colour));
    const cups = guided ? rec.cups : st.pack ?? 0;
    const packs = product ? packSizes(product) : PACKS.filter((p) => st.source !== 'pvc' || p <= 8);
    const isPvc = st.source === 'pvc';

    let price = 0;
    let compare = 0;
    let variantId = null;
    let quantity = 1;
    let available = true;
    let variantImage = '';
    if (cups) {
      const pack = resolvePack(product, st.colour, cups);
      if (pack) {
        price = pack.variant.price * pack.quantity;
        compare = pack.variant.compareAtPrice > pack.variant.price ? pack.variant.compareAtPrice * pack.quantity : 0;
        variantId = pack.variant.id;
        quantity = pack.quantity;
        available = pack.variant.available;
        variantImage = pack.variant.image || '';
      } else {
        const unit = isPvc ? FALLBACK_UNIT.cup + FALLBACK_UNIT.tee : FALLBACK_UNIT.cup;
        price = cups * unit;
        compare = isPvc ? price * FALLBACK_UNIT.pvcCompareRatio : 0;
      }
    }

    /** @param {number} n */
    const packPrice = (n) => {
      const pack = resolvePack(product, st.colour, n);
      if (pack) return pack.variant.price * pack.quantity;
      return n * (isPvc ? FALLBACK_UNIT.cup + FALLBACK_UNIT.tee : FALLBACK_UNIT.cup);
    };

    const ready = colourOk && Boolean(st.source) && (guided ? Boolean(rec.band) : Boolean(st.pack));
    return { guided, rec, entry, product, colourOk, cups, packs, isPvc, price, compare, variantId, quantity, available, variantImage, ready, packPrice };
  }

  // ------------------------------------------------------------------ render

  render() {
    const s = this.setup();
    const st = this.state;
    const t = this.config.text;

    // Gallery
    const main = this.querySelector('.lc-pdp__main');
    // A page opened from a Shopify ?variant= link (Shopping / ads) shows the resolved pack's own
    // image, matching the feed; a plain product URL keeps the colour's hero image as before.
    const usingVariantImage = Boolean(this.config.selectedVariantId && s.variantImage);
    const image =
      (usingVariantImage && s.variantImage) ||
      (s.colourOk && this.imageFor(st.colour ?? '')) ||
      this.imageFor(this.colourEntries()[0]?.name ?? '') ||
      this.config.fallbackImage ||
      '';
    // Pack variant images carry their own pack label top-left; the badges move to the bottom.
    this.querySelector('.lc-pdp__frame')?.toggleAttribute('data-lc-variant-image', usingVariantImage);
    // Swap only when the underlying file changes. The server-rendered <img> carries a responsive
    // srcset for the same file at a different width, so comparing full URLs (which differ only by
    // the width parameter) forced a second download of the image already on screen.
    if (main instanceof HTMLImageElement && image && !sameImageFile(main.currentSrc || main.src, image)) {
      main.removeAttribute('srcset');
      main.src = image;
    }
    // (No colour thumbnail row: the colour step in the buy box is the single colour control.)

    // Mode toggle
    const modes = this.querySelector('[data-lc-modes]');
    if (modes) {
      modes.innerHTML = [
        { key: 'direct', label: t.modeDirect || 'I know what I need' },
        { key: 'guided', label: t.modeGuided || 'Help me choose' },
      ]
        .map((m) => `<button type="button" class="lc-pdp__mode" data-action="mode" data-value="${m.key}" aria-pressed="${st.mode === m.key}">${esc(m.label)}</button>`)
        .join('');
    }

    // Steps
    const steps = this.querySelector('[data-lc-steps]');
    if (steps) steps.innerHTML = this.renderSteps(s);

    // Result
    const result = this.querySelector('[data-lc-result]');
    if (result) result.innerHTML = this.renderResult(s);

    this.renderSticky(s);

    // Meta line: descriptive text only. SKUs are not customer-facing, so neither the design's
    // LC-CUP-… codes nor the real Shopify SKUs are shown here.
    const meta = this.querySelector('[data-lc-sku]');
    if (meta) meta.textContent = t.skuDefault || 'Five colours, three pack sizes';

    // "In stock" badge follows the resolved variant's availability, not the product as a whole.
    const stockBadge = this.querySelector('[data-lc-stock-badge]');
    const inStock = s.variantId ? !!s.available : !!this.config.productAvailable;
    if (stockBadge instanceof HTMLElement) stockBadge.hidden = !inStock;

    // Delivery promise only makes sense for something that can be bought right now.
    const delivery = this.querySelector('[data-lc-delivery]');
    if (delivery instanceof HTMLElement) delivery.hidden = !inStock;
  }

  /** @param {ReturnType<LcPdpBuybox['setup']>} s */
  renderSteps(s) {
    const st = this.state;
    const t = this.config.text;
    const mobile = this.isMobile;
    const label = (text) => `<div class="lc-pdp__step-label">${esc(text)}</div>`;

    const sourceStep = `
      <div class="lc-pdp__step">
        ${label(t.step1 || '1. Setup type')}
        <div class="lc-pdp__sources">
          ${SOURCES.map((o) => `<button type="button" class="lc-pdp__option lc-pdp__source" data-action="source" data-value="${o.key}" aria-pressed="${st.source === o.key}"><span class="lc-pdp__option-label">${esc(o.label)}</span></button>`).join('')}
        </div>
      </div>`;

    const soldOut = t.soldOut || 'Sold out';
    const colourStep = (heading) => `
      <div class="lc-pdp__step">
        ${label(heading)}
        <div class="lc-pdp__colours">
          ${this.colourEntries()
            .map((c) => {
              const img = this.imageFor(c.name) || this.config.fallbackImage || '';
              const dead = !this.packAvailable(c.name, s.cups);
              const name = dead ? `${c.name} · ${soldOut}` : c.name;
              return `<button type="button" class="lc-pdp__option lc-pdp__colour${dead ? ' lc-pdp__option--unavailable' : ''}" data-action="colour" data-value="${esc(c.name)}" aria-pressed="${same(st.colour, c.name)}" aria-label="${esc(name)}" title="${esc(name)}" ${dead ? 'disabled aria-disabled="true"' : ''}>
                ${img ? `<img class="lc-pdp__colour-img" src="${esc(img)}" alt="" width="160" height="160" loading="lazy">` : ''}
                <span class="lc-pdp__colour-name"><span class="lc-swatch" style="background:${esc(c.hex || swatchHex(c.name))}"></span><span class="lc-pdp__colour-text">${esc(c.name)}</span></span>
                ${dead ? `<span class="lc-pdp__colour-soldout">${esc(soldOut)}</span>` : ''}
              </button>`;
            })
            .join('')}
        </div>
      </div>`;

    const colourHeading = s.colourOk && st.colour ? `${t.step3 || '3. Colour'}: ${st.colour}` : t.step3 || '3. Colour';

    if (!s.guided) {
      const packStep = `
        <div class="lc-pdp__step">
          ${label(t.step2Pack || '2. Pack size')}
          <div class="lc-pdp__packs">
            ${s.packs
              .map((n) => {
                const dead = this.packSoldOut(n);
                return `<button type="button" class="lc-pdp__option lc-pdp__pack${dead ? ' lc-pdp__option--unavailable' : ''}" data-action="pack" data-value="${n}" aria-pressed="${st.pack === n}" ${dead ? 'disabled aria-disabled="true"' : ''}><span class="lc-pdp__pack-label">${esc(packLabel(n))}</span><span class="lc-pdp__pack-price">${dead ? esc(soldOut) : this.money(s.packPrice(n))}</span></button>`;
              })
              .join('')}
          </div>
        </div>`;
      return sourceStep + packStep + colourStep(colourHeading);
    }

    // Guided
    const flockStep = `
      <div class="lc-pdp__step">
        ${label(t.step2Flock || '2. Flock size')}
        <div class="lc-pdp__packs">
          ${s.rec.bands.map((b, i) => `<button type="button" class="lc-pdp__option lc-pdp__flock" data-action="flock" data-value="${i}" aria-pressed="${st.flock === i}">${esc(b.label)}</button>`).join('')}
        </div>
      </div>`;

    if (!mobile) {
      return sourceStep + (st.source ? flockStep : '') + (s.rec.band ? colourStep(colourHeading) : '');
    }

    // Mobile: one step open at a time, completed steps collapse to a summary row.
    const e = st.mEdit;
    const m1 = !st.source || e === 'source';
    const m2 = !m1 && (st.flock === null || e === 'flock');
    const m3 = !m1 && !m2 && (!s.colourOk || e === 'colour');
    const row = (text, step) => `<div class="lc-pdp__done">${CHECK}<span class="lc-pdp__done-text">${esc(text)}</span><button type="button" class="lc-pdp__edit" data-action="edit" data-value="${step}">${esc(t.edit || 'Edit')}</button></div>`;
    const sourceLabel = st.source === 'pvc' ? 'PVC pipe' : 'Container / bucket';

    return (
      (m1 ? sourceStep : row(`Setup type · ${sourceLabel}`, 'source')) +
      (m2 ? flockStep : !m1 && st.flock !== null ? row(`Flock size · ${s.rec.band?.label ?? ''}`, 'flock') : '') +
      (m3 ? colourStep(colourHeading) : !m1 && !m2 && s.colourOk ? row(`Colour · ${st.colour}`, 'colour') : '')
    );
  }

  /** @param {ReturnType<LcPdpBuybox['setup']>} s */
  renderResult(s) {
    const st = this.state;
    const t = this.config.text;
    const eyebrow = s.guided ? t.resultGuided || 'Recommended for your flock' : t.resultDirect || 'Your setup';
    const title = s.ready
      ? `${packLabel(s.cups)} · ${st.colour} · ${s.isPvc ? 'PVC pipe' : 'Container'}`
      : t.notChosen || 'Not chosen yet';
    const waitLabel = !st.source
      ? t.waitSource || 'Select setup type to continue'
      : s.guided
        ? s.rec.band === null
          ? t.waitFlock || 'Select flock size to continue'
          : t.waitColour || 'Choose a colour to continue'
        : !st.pack
          ? t.waitPack || 'Select pack size to continue'
          : t.waitColour || 'Choose a colour to continue';

    const total = s.price * st.qty;
    const was = s.compare ? s.compare * st.qty : 0;
    const canBuy = s.ready && s.variantId && s.available && !st.busy;
    const ctaLabel = st.added ? t.addedLabel || '✓ Added to cart' : t.addLabel || 'Add to cart';

    return `
      <div class="lc-pdp__result-eyebrow">${esc(eyebrow)}</div>
      <div class="lc-pdp__result-title">${esc(title)}</div>
      ${s.guided && s.ready ? `<button type="button" class="lc-pdp__text-btn" data-action="mode" data-value="direct">${esc(t.override || 'Choose a different pack')}</button>` : ''}
      ${
        s.ready
          ? `<div class="lc-pdp__price-row">
               <span class="lc-pdp__price">${this.money(total)}</span>
               ${was ? `<span class="lc-pdp__was">${this.money(was)}</span><span class="lc-pdp__saving">Save ${this.money(was - total)}</span>` : ''}
             </div>
             ${!s.variantId ? `<div class="lc-pdp__error">${esc(t.noProducts || 'Connect the cups products in the theme editor to enable checkout.')}</div>` : ''}
             ${s.variantId && !s.available ? `<div class="lc-pdp__error">${esc(t.unavailable || 'This option is currently sold out.')}</div>` : ''}
             ${st.error ? `<div class="lc-pdp__error">${esc(st.error)}</div>` : ''}
             <div class="lc-pdp__buy-row">
               <div class="lc-pdp__qty" role="group" aria-label="Quantity">
                 <button type="button" data-action="qty" data-value="-1" aria-label="Decrease quantity">${MINUS}</button>
                 <span class="lc-pdp__qty-value">${st.qty}</span>
                 <button type="button" data-action="qty" data-value="1" aria-label="Increase quantity">${PLUS}</button>
               </div>
               <button type="button" class="lc-btn lc-btn--primary lc-btn--lg lc-btn--full lc-pdp__cta" data-action="add" ${canBuy ? '' : 'disabled'}>${esc(ctaLabel)}</button>
             </div>`
          : `<button type="button" class="lc-pdp__wait" disabled>${esc(waitLabel)}</button>`
      }`;
  }

  // ------------------------------------------------------------------ events

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;
    const { action, value = '' } = target.dataset;

    switch (action) {
      case 'mode':
        this.chooseMode(value === 'guided' ? 'guided' : 'direct');
        break;
      case 'source':
        this.chooseSource(value);
        break;
      case 'flock':
        this.setState({ flock: Number(value), mEdit: null, added: false });
        break;
      case 'pack':
        if (this.packSoldOut(Number(value))) break;
        this.setState({ pack: Number(value), added: false });
        break;
      case 'colour':
        if (!this.packAvailable(value, this.setup().cups)) break;
        this.setState({ colour: value, mEdit: null, added: false });
        break;
      case 'edit':
        this.setState({ mEdit: value });
        break;
      case 'qty': {
        const next = Math.min(99, Math.max(1, this.state.qty + Number(value)));
        this.setState({ qty: next, added: false });
        break;
      }
      case 'add':
        this.#add();
        break;
      default:
        break;
    }
  };

  /** @param {'direct' | 'guided'} mode */
  chooseMode(mode) {
    if (mode === 'direct') {
      const source = this.config.currentSource === 'pvc' ? 'pvc' : 'container';
      this.state.source = source;
      const entry = this.entryFor(this.config.currentColour) ?? this.colourEntries()[0] ?? null;
      const sizes = packSizes(this.productFor(entry));
      const colour = this.config.currentColour || entry?.name || null;
      this.setState({ mode, source, flock: null, colour, pack: this.firstAvailablePack(colour, sizes) ?? PACKS[0], mEdit: null, added: false });
    } else {
      this.setState({ mode, source: null, flock: null, colour: null, pack: null, mEdit: null, added: false });
    }
  }

  /** @param {string} key */
  chooseSource(key) {
    this.state.source = key;
    const allowed = this.colourEntries().map((c) => c.name);
    const keep = this.state.colour && allowed.some((n) => same(n, this.state.colour));
    const colour = keep ? this.state.colour : allowed[0] ?? null;
    const sizes = packSizes(this.productFor(this.entryFor(colour)));
    const pack =
      this.state.pack && sizes.includes(this.state.pack) && this.packAvailable(colour, this.state.pack)
        ? this.state.pack
        : this.firstAvailablePack(colour, sizes) ?? PACKS[0];
    this.setState({ flock: null, colour: keep ? this.state.colour : null, pack: this.state.mode === 'direct' ? pack : null, mEdit: null, added: false });
  }

  // ------------------------------------------------------------------ cart

  async #add() {
    const s = this.setup();
    if (!s.ready || !s.variantId || !s.available) return;
    const items = [{ id: s.variantId, quantity: s.quantity * this.state.qty }];
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');

    this.setState({ busy: true, error: '' });
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections }),
      });
      if (!response.ok) throw new Error('The cart is temporarily unavailable. Please try again.');
      const json = await response.json();
      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      const cart = await cartResponse.json();
      if (json.status) {
        this.dispatchEvent(new CartErrorEvent({ error: json.message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'lc-pdp-buybox', sourceId: this.id, itemCount: items[0].quantity } });
        throw new Error(json.description || json.message || 'Add to cart failed');
      }
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'lc-pdp-buybox', sourceId: this.id, itemCount: items[0].quantity, sections: json.sections, didError: false } });
      this.setState({ busy: false, added: true });
    } catch (error) {
      deferred.reject(error);
      this.setState({ busy: false, error: error instanceof Error ? error.message : String(error) });
    }
  }
}

if (!customElements.get('lc-pdp-buybox')) customElements.define('lc-pdp-buybox', LcPdpBuybox);
